import React, { useState } from "react";
import { KeyRound, X, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Confirmation modal for the "Resetear contraseña" superadmin action.
 *
 * The SA never sees the generated token or the new password — those
 * stay strictly on the email channel. This modal collects:
 *   - explicit confirmation (because the action invalidates the
 *     target user's active sessions immediately)
 *   - an optional free-text reason that gets recorded in the audit
 *     log (NOT forwarded to the user's email — it's an operational
 *     note, surfacing it would be a context leak)
 *
 * The actual POST + toast lives in `useUserActions.handleResetPassword`
 * so this component stays presentational.
 */
export function ResetPasswordModal({ user, submitting, onConfirm, onClose }) {
  const [reason, setReason] = useState("");

  if (!user) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    onConfirm({ reason: reason.trim() });
  };

  return (
    <div
      data-testid="reset-password-modal-backdrop"
      className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        data-testid="reset-password-modal"
        className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl"
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <KeyRound size={18} className="text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base">
                Resetear contraseña
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                El usuario recibirá un email con un link para definir una nueva contraseña.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="reset-password-modal-close"
            className="p-1 text-slate-500 hover:text-slate-200 hover:bg-slate-800 rounded"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div
            data-testid="reset-password-target-info"
            className="rounded-lg bg-slate-800/60 border border-slate-700 px-4 py-3"
          >
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-1">
              Usuario
            </p>
            <p className="text-white font-medium">{user.name || "—"}</p>
            <p className="text-sm text-slate-400">{user.email}</p>
          </div>

          <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 px-4 py-3 flex gap-3">
            <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200 leading-relaxed">
              Las sesiones activas del usuario serán <strong>cerradas inmediatamente</strong>
              {" "}en todos los dispositivos.
            </p>
          </div>

          <div>
            <label
              htmlFor="reset-password-reason"
              className="block text-xs text-slate-400 font-medium mb-1.5"
            >
              Razón (opcional, queda en auditoría)
            </label>
            <textarea
              id="reset-password-reason"
              data-testid="reset-password-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              maxLength={500}
              rows={3}
              placeholder="Ej. ticket SUP-1234, usuario reporta no recibir su email de reset"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-teal-500 resize-none"
            />
            <p className="text-[10px] text-slate-600 mt-1">
              No se incluye en el email del usuario.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-800 bg-slate-900/50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="reset-password-cancel-btn"
            className="px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="reset-password-confirm-btn"
            className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <KeyRound size={14} />
                Confirmar reseteo
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
